import { AgTechQueryBuilder } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { SaleInvoiceModifierDialogTourConfig } from './SaleInvoiceModifierDialogTourConfig';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormEnumDropdownControl } from '@agtech/web/components/Forms/AgTechDropdowns';
import { AgTechFormNumberControl } from '@agtech/web/components/Forms/Controls/AgTechFormNumberControl';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import { AgTechFormToggleControl } from '@agtech/web/components/Forms/Controls/AgTechFormToggleControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { SaleInvoiceModifier, DefaultSaleInvoiceModifier, ChargeDiscount } from '@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierData';
import { useSaleInvoiceModifierDataWriteInvoiceModifierAction } from '@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierDataActions';
import React from 'react'

export type SaleInvoiceModifierDialogProps = {
    InvoiceModifier?: SaleInvoiceModifier
}

const SaleInvoiceModifierDialog = (props: SaleInvoiceModifierDialogProps) => {
    const writeInvoiceModifierAction = useSaleInvoiceModifierDataWriteInvoiceModifierAction();

    return (
        <AgTechFormDialog
            width={450}
            structure={() => ({
                header: {
                    header: props.InvoiceModifier ? 'Update charge or discount - ' + props.InvoiceModifier.name : 'Create charges and discounts',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={SaleInvoiceModifierDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: props.InvoiceModifier ? 'Update' : 'Create',
            })}
            dialogData={{
                query: AgTechQueryBuilder.buildQueryResult({
                    data: props.InvoiceModifier ?? DefaultSaleInvoiceModifier
                }),
                getDialogData: d => d
            }}
            dialogAction={{
                action: writeInvoiceModifierAction,
                getActionData: d => ({
                    InvoiceModifier: d
                })
            }}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <div id='invoice-modifier-name'>
                    <AgTechFormRow>
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="Name"
                            required={true}
                            focused={true}
                            value={entity => entity.name}
                            width={99}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                name: val
                            })}
                        />
                        <AgTechFormEnumDropdownControl
                            entityType={formContext.entity}
                            caption='Category'
                            isRequired={true}
                            enum={ChargeDiscount}
                            selectedValue={formContext.entity.modifierType}
                            onChanged={async (entity, enumValue) =>({
                                ...entity,
                                modifierType: enumValue
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='invoice-modifier-amount'>
                    <AgTechFormRow>
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption="Amount"
                            required={true}
                            value={entity => entity.amount}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                amount: val
                            })}
                            validation={{
                                validateOnSubmit: (ent, val) => {
                                    let InvoiceModifierAmountValidationMessage = '';

                                    if (ent.isPercentage && val > 100) {
                                        InvoiceModifierAmountValidationMessage = "A percentage charge or discount cannot exceed 100%";
                                    }
            
                                    return InvoiceModifierAmountValidationMessage;
                                }
                            }}
                        />
                        <AgTechFormToggleControl
                            entityType={formContext.entity}
                            caption="Is Percentage?"
                            value={entity => entity.isPercentage ?? false}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                isPercentage: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

SaleInvoiceModifierDialog.Open = (e: AgTechClickEventHandler, props: SaleInvoiceModifierDialogProps) => {
    e.dialogs.openDialog({
        dialog: () => <SaleInvoiceModifierDialog InvoiceModifier={props.InvoiceModifier} />
    });
}

SaleInvoiceModifierDialog.Button = (props: SaleInvoiceModifierDialogProps & AgTechButtonProps) => {
    return (
        <AgTechButton
            text={props.InvoiceModifier ? 'Update charges and discounts' : 'Create charges and discounts'}
            icon={props.InvoiceModifier ? 'fas fa-edit' : 'fas fa-plus'}
            {...props}
            action={e => SaleInvoiceModifierDialog.Open(e, props)}
        />
    )
}

export default SaleInvoiceModifierDialog